import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const voiceSalesHomeMoveMachineConfig = {
  id: 'voiceSalesHomeMoveMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.voiceSalesHomeMove
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ]
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'home'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'psrCapture',
            description: `and(['hasPSR', not('releaseRevampJourney')])`,
            guard: and(['hasPSR', not('releaseRevampJourney')])
          },
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    psrCapture: {
      on: {
        NEXT: {
          target: 'userPreferences'
        }
      },
      meta: {
        path: routePath.psrCapture
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    authResponse: {
      meta: {
        path: routePath.authResponse
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDcD2BLAxmAygQwBs4AJVAWzAFlVkq9MALdAOzADoHywBiAOQFEAGgBUA2gAYAuolAAHVLHQAXdKmYyQAD0QBGAOwAmAGxsArHr3iAnABYd+20ZsAaEAE9dOgMx6zxrwEAHFYGXsE2AL4RrmhYuIQkXNS0lPRMrBxcfEJiOtJIIPKKKmoa2gj6xmYW1nYONk6uHgiGgWyB9npWpgbioTZWUTEY2PhEsKQUyXSMLOycFNwAQgAyAPIAwgDSAPoAUmsAqgBKAgCaEvlyCsqq6gXllSbmlradji7uiD46bFZ6NnE4hsplMOhC3SGIFiowSEySNBm6XYACMAK6KViwWCHWQQPBKOAAQWYzFQaOY2AozCU2RElw0RVupQeiAMBhsNnaFkCoVMHS87M+zS8RhMXhCRkM5lF-x0pihMPi40mVERqVmGXRmLgOLxBOJpPJlLA1NpRJW6wA6vsjqd+BcpIybiV7qBHgC2mEjME9IEemE9EYmt92WwdDZeToDOCGj0dEZFSNlYkpuq0nM2Nq5tjcfjCbAcGiUWRlPwyHh0AQ6WInQUma6ymzeVzzLyOuJTFZu4EQwhReLJdK9LK9PKk3Exqm1SkMxkAGZosAEYRuWQ8AT0uvXYp3JsIIVcwI8vkCoV9wwmBoGSyA6PGfQT2EqhGzzXsRfL1frmuiPLO3cWXdNkOSPE8vH5bxzy+BBghMSxxD9BoOksAxAifFN4TTN9kTYT8VzXDcclEAwrkKF091ZA9QO5P1Tygjk+0jExAk7QVTCMTsAX5DCpywmckUzfDvx4DZiCJXgAHF+B2IkABE5OOfgcBwBl6wooCtF0Ec2h0f0dHEQVxClDjgxgqU2isQIjA5LsrATIw9N4uFVWmDVcIoQkACd5BYJQADVCHQfM7l-bdyMAt0tIPFtqnbAy7Ksi8gzYNDeVMQV2VsQJ0OiaFkz41z03fNhPLAHyMBpQKCGCglQs3XIyIbSjgJijk4rQhLuySmDLzYa9bDHfl9IVPKlUK19BIyMqKr86ratdbhJMOABJOSZI2FYVu2NSd2ZKLyjQ9q206ztut7czENShw-SMHwujsZyX2wqb2FgZcwEwJQ1lkV1YG4fgtsklbVhkgAxQ5Ad2iL9v3LwGi8NgxSMboAhRkIGj7bsuRsAwrJBLw9LBQmnunNy5zej6vp+v6AbklbhFkhSlJU6Hms0w6Ql8McnHBINBSsIxTD7Az9HDbwfXEazDMBXLhknFzJvczN3qIanfruf7iDWSgZMoNZ-P4NmNIO74EaRsVUdlDGzOaAzbDYKXah8CUcp9Un+PJkrVc+76NbULWdb1g2jf-dTIrh83kat9GOVt3QYxx8RowGVi3lG+XnzJ4rcJ99Xae13Wdn1w2SKak3I7ui2UYy6245F4EuQMUF5W8XlHI9oqcMzWQ8DcM1KDAJROAgMLy4jqifDscMe3hxCwnrmCOjYTKkNBS8gUGMaCsVl7lYyKmlC8rB-LAJhMCIMeANhqiY0sqyUMjAMARFyNEcJwxDOM4EfQz-KFeegJfe7BD7H0wKfc+l8Gp-nHjfVqd8-gP30E-UIL8YLRm7GYQIXhk56Ayv8SE28AHZ27gfNWR8T5nywJfY2E94F6UQTlZB-pUHCl0PDKw-UpaClFIZYIoRO5KwpmwMAyBhB4GPvOecK1mDzlQF5Csi1oHhXZqbWCPp+pjksC8fk-I9Cv2wSvSoX9OLIS8IIvewjMABzRGQf2zB+A0i8m4K+4c4HRW8BKFeQZTDGQMvDJwVhX4WDYCODiN4rAQScF2CxQCrE2Lsa6RxR8XHQLDntRsVFgicMFnBYEtgAw6BFhglenIOg9H+GdcxRCs6exzj3cqsA1CEBkXIhRdU1BFjIBWZxriMktWitZNoNgtFcTwf6Y8F5RSO04oZdGYQwg6FiV7XC64vJNOYC02R8jFF3C6T0lxYkJLSSZopZSqkVEVyyRokZ+gxm6MmeZayqV4agTukLDuNTMJd1emwNZGytltN2Z02xBzuBHKkuDSGKwdjCDOAABSNpcuhgybmjJ0RM-RMFei-DCTYHw-JIlSz-uNXecSSr-OaQQVpOyOnMH2RIw54lIU7A2GsXgOBDiUHhcIFa7LaHuPKEMzRdyMV6OKV0cMlhowS3sKZZZ9SMgAEc0SoEJH0mGmTWpPGqK8OoD1GjoIyuIP49tATWHtgYBVpD2AqrVTwfglAiQrRhQARUOGsYQSLYFao8X1F4tR3gNDYRUEZbQgRoTsmCeUMSvkTUsSVO16q2Ucq5TyvlvABW+seLjNoYJOzJ2MhyW6r8bymqBH6QwAxhrWt+UmngEMobIsFdpKoAa3j1ENXbHwXowRBnlHjP04441kpWZmJQEj0BSLkkPSsBB-rKJ9QMw6NFjx0QgmeRiRqcHltsJvS1tbgEiIrFWV1qr1WLuvtm3QgRAQrxCKEbwnIn5eFLbpCwhNEKAmQnLf+tSflHswF5MABoiSYGsRSWkl63HXraq2OiHZEoXTtj0UwK88aCkBHdAyAxD1WOA6B8DxpaQQpOfJM5rNm2wf6Hm7odgGiOBFs3XwkTgjWQxvi39pLAFjoyBAdAwGvozpRMoESGrVGV0RtHWusdMbbtxZyG8rzFOgjwyVfjgmlDCdE4RbgWbl1myrtJtG3ZF523xWh3NFbDASgBGp3CGnfbaag8RKjBmEB2EJmYcwVkOJhFBMLbdJr7K1H3dYK1I6eOKvYI5oTYARO0n0xzRAnnfjr183df0oJX6mC5NYV5vR4yZXsz3WAXlkm9Og-05LHnOTihw+8TxRT0GAk4WKN+MYeh2BJTvKLNq-llYq6k4i6TNXueffV4EjWAjNZQzuxCQZjJ4IsDWyLJDfmyDKxsPAv00TAfE1c1qIyrJ-HhuCJwxhsEGFftYC2HWB3dZKxkDE5V4XAfnOVMAJoF2uaXTV3xeDQk4Pspd-Qr6jVhl5JEjkxlP24bW3U-r8iIDlQZZV37V73MZTvaxLK3gQjw3ByhyHGGYfYfyVEPKZIUfwAKNx9bwDMc1YALTxwQKzx2QIufc652hJ78wuBM7UQmYyZgiWhEHWxZDiBWh-C6LezkAQhRb0zt8oRJVsxYj1PmQ0ZIKRUi+0oIX+5wQZXDP6RCWWi24z7AOMwnFZmPpvB0RMCOAPCM17qPMBpCzFlLEocsc7je31BIjLsPQbJIX0Hg23Yp7ffz6ITZ3CZ+dZgxDmbXPuiwljLCeggRZwO6mD61JPvwIzErB94ewsfngO4jUnodrvVfxvJbhYShFi-RV6M3UJHJo8rdAli5ofU7qIXjMZVjkQ3fq48kPcqvkqpBRCppCTt9ZntGMCjJPhNcYhpHzg48kYEz4ubqnma8LKpKEkmiYKYBO-lFL+GWWfbCaE1m4gTivhvQRlsHYQW8Pm9R1os2A84-Y-p79QwrpOsLVcYhYIJike8nAAR-Bm40JuxU9e5+5DdB5h5UAIAID+xrJEZTNJQUZggegsZeRvNOxIweZ2MItAC+tflQFKFIE78YNxtrt0EIJfB2RMNuEwRTNU9RFxFJFpFtl2lfVV9WobIuQJRIkVs+grI0IDFEZIxIxjxzAIwuwGC-01cE1cJrFmBYBbF7EhsCD7AGhdVEI74f8pZX4UYN9wRWJkJv1qlGCGdhFKVNlqUJDgV6VQVGUCD+R354YssOhi1bApk2hDAug7owR4x0Dp8DDMx60LDOJLM7pxl7wEwKCWtDB0M9IEwstuIetiFEdfkJ0xCZ0J0qxadqs1Fu94I+ROgbI8YbwRYMpfhLdrIxQU47NkjW9MxTQ50z17ULDUZzcAcrdZCuDzMCjCU7ln1QROxU8gMQNCQwMIMaQCDm4cp+ocpjw9JI0gQ2cB1LN-hm5OwFdrI9BU9YstN4sdN1wCDV5+oEirtv1ORAticLjpRriykpR7iBMnMnijcOCasJQy0pR7B-hdFug5jdAusLYJRKho1scMDBsnFmgGj9wT8ck-MxxuwOJwROjQh2gcEQj7I6Jk5MSvJttdtgMCCGgbJTUWF4Y6IepzNbsrsmEFk8Z7BU8XsvI3swAPtgNvtmSrBk5e9bxRQBZDAySDBEF-j+RAS7jBjeN2BkdUdAjnFgj9A81UEuwcpbogkIdlTuxVSbigTNTgCdT6TyBZAiBCRgjFcxdiZ-R8ZsElSVSri1TmINSPCKij0wBNBvIfCNg1AJ0vpDh6ixtISiDEF2jLZyDESKgSkBgsjy9k5sF3C9CW8tSRFwzyofClgoANgCA8BsQsAflXikySDN8rIESG5OR+pH1sYLA0JbTgz3cSowyIzCBpgZF6yq5GzUyWz0FQgTUP4QgI9lNKcIggA */
      ...voiceSalesHomeMoveMachineConfig
    },
    {
      guards
    }
  );
